/// <reference path="./api/IModuleBootstrap.ts"/>
/// <reference path="./api/IModuleServices.ts"/>
/// <reference path="../util/ScriptLoader.ts"/>
/// <reference path="../util/ConstructorFromString.ts"/>
/**
 * Created by bjarne on 2017-03-14.
 */
module com.quickspin.module {

	import IModuleBootstrap = com.quickspin.module.api.IModuleBootstrap;
	import ClassFromString = com.quickspin.util.ConstructorFromString;
	import IModuleServices = com.quickspin.module.api.IModuleServices;
	import ScriptLoader = com.quickspin.util.ScriptLoader;

	/**
	 * Wraps a ScriptLoader to handle dynamic loading based on an object 
	 * formatted like this:
	 * {
	 * achievements: {
	 *  looks: {
	 *      type: "css",
	 *      URL: "keypad-dom-achievements/assets/hamlin/hamlin_desktop.css",
	 *      minifiedURL: "keypad-dom-achievements/assets/hamlin/hamlin_desktop.min.css"
	 *  },
	 *  code: {
	 *      type: "js",
	 *      URL: "qs-core-achievements/com.quickspin.web.qs-core-achievements.js",
	 *      minifiedURL: "qs-core-achievements/com.quickspin.web.qs-core-achievements.min.js"
	 *  }
	 * }
	 */
	class ModuleDataModel {
		id:string;
		activated:boolean;
		className:string;
		parameters:any[];
		bootstrap:IModuleBootstrap;
	}

	export class ModuleManager implements IModuleServices {

		private static _instance:ModuleManager;
		private static _creating:boolean;

		private _config:any;
		private _doneCallback:any = null;
		private _failCallback:any = null;
		private _options:any;
		private _modules:ModuleDataModel[] = [];
		private _services:any = {};
		private _isModuleBootUpPhaseDone:boolean = false;

		constructor() {
			if (!ModuleManager._creating) {
				throw new Error("ModuleManager class is a singleton, explicit creation not supported, call ModuleManager.getInstance() instead.");
			}
		}

		static getInstance():ModuleManager {
			if (!ModuleManager._instance) {
				ModuleManager._creating = true;
				ModuleManager._instance = new ModuleManager();
				ModuleManager._creating = false;
			}
			return ModuleManager._instance;
		}

		addService(id:string, service:any):void {
			if (this.hasService(id)) {
				throw Error("ModuleManager.addService: Service '" + id + "' already exists");
			}

			this._services[id] = service;
			// console.log("ModuleManager.addService: Service '" + id + "' added");

			if (!this._isModuleBootUpPhaseDone) {
				return; // bootstrapped modules will receive added services only after the boot up phase is done
			}

			// the _modules array is complete; it contains all bootstrappable modules that have booted up successfully
			for (let b:number = 0; b < this._modules.length; ++b) {
				let bs:IModuleBootstrap = this._modules[b].bootstrap;
				if (bs) { // Not all modules has actual bootstraps
					try { // Not all modules implements IModuleBootstrap fully
						bs.setService(id, this._services[id]); // might imply that additional services will be added
					} catch (e) {
						console.warn("ModuleManager.addService: Module '" + this._modules[b].id + "' does not implement setService of the IModuleBootstrap interface: service '" + id + "' can't be provided. Error message: " + e.message);
					}
				}
			}
		}

		hasService(id:string):boolean {
			return this._services.hasOwnProperty(id);
		}

		getService(id:string):any {
			if (!this.hasService(id)) {
				throw Error("ModuleManager.getService: Service '" + id + "' does not exist");
			}
			return this._services[id];
		}

		/**
		 * @param dynamicallyLoadedModulesConfig See class documentation for object format
		 * @param options Currently supported options: debug:boolean, relativePath:string
		 * @param doneCallback After getting this callback the caller can be assured that the
		 * contents of the loaded files are available to be executed.
		 * @param failCallback Looks like there was a missing or inaccessible file
		 */
		public load(dynamicallyLoadedModulesConfig:any, options:any = {debug: false}, doneCallback:Function = null, failCallback:Function = null):void {
			this._config = dynamicallyLoadedModulesConfig;
			this._options = options;
			this._doneCallback = doneCallback;
			this._failCallback = failCallback;

			if (!this._config) {
				this.onLoadComplete();
				return;
			}

			let loader:ScriptLoader = new ScriptLoader();
			for (let moduleID in this._config) {
				if (this._config.hasOwnProperty(moduleID)) {

					// Module level
					let moduleObject:any = this._config[moduleID];

					// Is the modules configuration enabled
					let enabledPropertyName:string = "enabled";
					if (moduleObject.hasOwnProperty(enabledPropertyName) && (moduleObject[enabledPropertyName] === true || moduleObject[enabledPropertyName] === "true")) {
						// What version of the module(if any)?
						let moduleConfigVariantName:string = this._options.debug ? "debug" : "release";
						if (moduleObject.hasOwnProperty(moduleConfigVariantName)) {
							let moduleConfigVariant:any = moduleObject[moduleConfigVariantName];

							this.extractBootrap(moduleID, moduleConfigVariant);

							if (moduleConfigVariant.hasOwnProperty("resources")) {

								// Resources level
								let resourcesObject:any = moduleConfigVariant.resources;

								for (let fileID in resourcesObject) {
									if (resourcesObject.hasOwnProperty(fileID)) {

										// File level
										let fileObject:any = resourcesObject[fileID];
										loader.add(fileObject.URL, fileObject.type);
									}
								}
							}
						}
					}
				}
			}
			loader.load(() => this.onLoadComplete(), () => this.onLoadFail());
		}

		// You may want to load resources without actually having a bootstrap attached
		private extractBootrap(id:string, moduleObject:any):void {
			let bsdm:ModuleDataModel = new ModuleDataModel();
			bsdm.id = id;
			bsdm.activated = false;
			bsdm.className = moduleObject.bootstrap;
			let params:any = moduleObject.bootstrapParameters;
			if (params) {
				bsdm.parameters = params;
			} else {
				bsdm.parameters = [];
			}
			this._modules.push(bsdm);
		}

		private onLoadComplete(): void {

			// Instantiate the bootstraps
			for (let i:number = 0; i < this._modules.length; i++) {
				let bsdm:ModuleDataModel = this._modules[i];
				if (bsdm.activated === true) {
					continue;
				}

				try {
					if (bsdm.className) {
						let dynamicClass = ClassFromString.getClass(bsdm.className);
						bsdm.bootstrap = new (Function.prototype.bind.apply(dynamicClass, [this, this._options].concat(bsdm.parameters)));
					}
				} catch (e) {
					console.warn("ModuleManager.onLoadComplete: unable to create bootstrap '" + bsdm.id + "' using class '" + bsdm.className + "'. Error message: " + e.message);
				}
			}

			// Trigger bootUp on all bootstraps
			for (let i:number = 0; i < this._modules.length; i++) {
				let bsdm:ModuleDataModel = this._modules[i];
				if (bsdm.activated === true) {
					continue;
				}

				try {
					bsdm.activated = true;
					if (bsdm.bootstrap) {
						bsdm.bootstrap.bootUp(this);
					}
				} catch (e) {
					console.warn("ModuleManager.onLoadComplete: unable to call bootUp '" + bsdm.id + "' using class '" + bsdm.className + "'. Error message: " + e.message);
				}
			}

			// the _modules array is complete; it contains all bootstrappable modules that have booted up successfully =>
			// now it's time for providing added services to these modules,
			// note that this might imply that additional services will be added
			this._isModuleBootUpPhaseDone = true;
			for (let b:number = 0; b < this._modules.length; ++b) {
				let bsdm:ModuleDataModel = this._modules[b];

				const ids:string[] = Object.keys(this._services);
				for (let i:number = 0; i < ids.length; ++i) {
					const id:string = ids[i];
					if (bsdm.bootstrap) {  // Not all modules has actual bootstraps
						try { // Not all modules implements IModuleBootstrap fully
							bsdm.bootstrap.setService(id, this._services[id]); // might imply that additional services will be added
						} catch (e) {
							console.warn("ModuleManager.onLoadComplete: Module '" + bsdm.id + "' does not implement setService of the IModuleBootstrap interface: service '" + id + "' can't be provided. Error message: " + e.message);
						}
					}
				}
			}

			if (this._doneCallback) {
				this._doneCallback();
			}
		}

		private onLoadFail():void {
			if (this._failCallback) {
				this._failCallback();
			}
		}
	}
}